import { useEffect, useState } from "react";
import api, { formatDate } from "@/lib/api";
import { Loader2, RefreshCw, CheckCircle2, XCircle, Eye, X, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const map = {
    SENT: "bg-[#D1FAE5] text-[#059669]",
    FAILED: "bg-[#FEE2E2] text-[#DC2626]",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || "bg-[#F1F5F9] text-[#475569]"}`}>
      {status === "SENT" ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {status}
    </span>
  );
};

export default function Logs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [retryId, setRetryId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const params = {
        page,
        page_size: pageSize,
        ...(filter ? { status: filter } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      };
      try {
        const { data } = await api.get("/logs", { params });
        if (alive) {
          if (data && data.items) {
            setItems(data.items);
            setTotal(data.total);
            setTotalPages(data.total_pages);
          } else {
            setItems(Array.isArray(data) ? data : []);
            setTotal(Array.isArray(data) ? data.length : 0);
            setTotalPages(1);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [filter, search, page, pageSize, reloadKey]);

  const handleRetry = async (log) => {
    setRetryId(log.id);
    try {
      const { data } = await api.post(`/logs/${log.id}/retry`);
      if (data.ok) {
        toast.success("Pesan berhasil dikirim ulang");
      } else {
        toast.error("Retry gagal");
      }
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setRetryId(null);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="logs-page">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]">Message Logs</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Riwayat lengkap pengiriman pesan WhatsApp via Pancake.id.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Cari invoice, pelanggan, no WA..."
            className="w-full bg-white border border-[#E2E8F0] rounded-md pl-9 pr-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(1); }}
          data-testid="logs-filter"
          className="bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
        >
          <option value="">Semua Status</option>
          <option value="SENT">Terkirim</option>
          <option value="FAILED">Gagal</option>
        </select>
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          data-testid="logs-refresh"
          className="bg-white border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8F9FA] rounded-md px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA]">
            <tr className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
              <th className="px-6 py-3 text-left">Waktu</th>
              <th className="px-6 py-3 text-left">Invoice</th>
              <th className="px-6 py-3 text-left">Pelanggan</th>
              <th className="px-6 py-3 text-left">No. WA</th>
              <th className="px-6 py-3 text-left">Template</th>
              <th className="px-6 py-3 text-center">Status</th>
              <th className="px-6 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {loading && (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-sm text-[#64748B]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Memuat...
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-sm text-[#64748B]">
                  Belum ada log pesan.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((log) => (
                <tr key={log.id} className="hover:bg-[#F8F9FA]">
                  <td className="px-6 py-3 text-[#475569]">{formatDate(log.sent_at)}</td>
                  <td className="px-6 py-3 font-mono text-xs text-[#0F172A]">{log.invoice_number}</td>
                  <td className="px-6 py-3 text-[#0F172A]">{log.customer_name}</td>
                  <td className="px-6 py-3 font-mono text-xs text-[#475569]">{log.customer_phone}</td>
                  <td className="px-6 py-3 text-[#475569]">{log.template_key}</td>
                  <td className="px-6 py-3 text-center">
                    <StatusBadge status={log.status} />
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => setPreview(log)}
                        data-testid={`view-log-${log.id}`}
                        className="text-[#475569] hover:text-[#0066FF] transition-colors"
                        title="Lihat pesan"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {log.status === "FAILED" && (
                        <button
                          onClick={() => handleRetry(log)}
                          disabled={retryId === log.id}
                          data-testid={`retry-log-${log.id}`}
                          className="text-[#DC2626] hover:underline text-xs font-medium disabled:opacity-50"
                        >
                          {retryId === log.id ? "..." : "Retry"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* Pagination controls */}
        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-white flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#64748B]">
          <div className="flex items-center gap-3">
            <span>
              Menampilkan {total === 0 ? 0 : (page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} dari {total} log
            </span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="bg-white border border-[#E2E8F0] rounded px-2 py-1 text-xs text-[#0F172A] outline-none focus:border-[#0066FF]"
            >
              <option value={10}>10 / halaman</option>
              <option value={20}>20 / halaman</option>
              <option value={50}>50 / halaman</option>
              <option value={100}>100 / halaman</option>
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-2.5 py-1.5 border border-[#E2E8F0] rounded bg-white hover:bg-[#F8F9FA] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 text-[#0F172A] font-medium transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
            <span className="px-3 py-1 font-medium text-[#0F172A]">
              Halaman {page} dari {totalPages || 1}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="px-2.5 py-1.5 border border-[#E2E8F0] rounded bg-white hover:bg-[#F8F9FA] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 text-[#0F172A] font-medium transition-colors"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white border border-[#E2E8F0] rounded-lg max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="log-preview-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-sm font-medium text-[#0F172A]">Pesan ke {preview.customer_name}</div>
                <div className="text-xs text-[#64748B] font-mono">{preview.customer_phone}</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-[#64748B] hover:text-[#0F172A]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <pre className="text-xs text-[#475569] font-mono whitespace-pre-wrap bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3 leading-relaxed max-h-96 overflow-auto">
              {preview.message}
            </pre>
            {preview.error && (
              <div className="mt-3 text-xs text-[#DC2626] bg-[#FEE2E2] rounded-md px-3 py-2">
                Error: {preview.error}
              </div>
            )}
            {preview.raw?.request_curl && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-[#475569] mb-1">CURL Request (Debug)</div>
                <pre className="text-[10px] text-[#64748B] font-mono whitespace-pre-wrap bg-[#F1F5F9] border border-[#E2E8F0] rounded-md p-2 max-h-32 overflow-auto select-all">
                  {preview.raw.request_curl}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
